/* Requests — churches waiting to be approved.
 *
 * Somebody has registered as the administrator of a church and is waiting to be
 * let in. Until this is decided their church cannot be used, so an empty queue
 * is the healthy state and a full one is work.
 *
 * SUPER ADMIN ONLY. A Church Admin reaching this gets a 403, which becomes an
 * explanation rather than an error: they have not done anything wrong, the page
 * simply is not theirs.
 */
import { useState } from 'react'
import { AlertTriangle, Check, Loader2, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Confirm } from '@/components/ui/confirm'
import { ApiError } from '@/api/client'
import { useChurchRequests, useDecideRequest } from '@/api/console'
import type { ChurchRequest } from '@/types/api'

export function RequestsPage() {
  const requests = useChurchRequests()

  return (
    <div className="space-y-s4">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Requests</h1>
      </header>

      {requests.isLoading ? (
        <Card className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </Card>
      ) : null}

      {requests.isError ? (
        <Card className="px-s4 py-s6 text-center text-base text-ink-2">
          {requests.error instanceof ApiError && requests.error.status === 403
            ? 'Approving churches is for Super Admins.'
            : 'Could not load requests. Check your connection and try again.'}
        </Card>
      ) : null}

      {!requests.isLoading && !requests.isError && !requests.data?.length ? (
        <Card className="px-s4 py-s6 text-center text-base text-ink-2">
          Nothing waiting.
        </Card>
      ) : null}

      {requests.data?.length ? (
        <div className="space-y-s3">
          {requests.data.map((r) => (
            <RequestCard key={r.request_id} request={r} />
          ))}
        </div>
      ) : null}
    </div>
  )
}

function RequestCard({ request }: { request: ChurchRequest }) {
  const decide = useDecideRequest()
  const [pending, setPending] = useState<'approve' | 'reject' | null>(null)

  return (
    <>
      <Card className="flex flex-wrap items-start justify-between gap-s4 p-s4">
        <div className="min-w-0">
          <h2 className="text-md font-bold">{request.church_name}</h2>
          <p className="mt-1 text-base text-ink-2">
            {[request.city, `${request.plan} plan`].filter(Boolean).join(' · ')}
          </p>
          <p className="mt-s2 text-base">
            <span className="font-semibold">{request.admin_name}</span>
            {request.admin_mobile ? ` · ${request.admin_mobile}` : ''}
          </p>
          {request.admin_email ? (
            <p className="text-sm text-ink-2">{request.admin_email}</p>
          ) : null}

          {/* The one thing an approver cannot work out for themselves: whether
              the person asking is the person the church was created for.
              Slugs are guessable, so a stranger CAN reach this queue — and
              approving them hands over somebody else's church. */}
          {request.matches_invite === false ? (
            <p
              role="alert"
              className="mt-s3 flex items-start gap-s2 rounded border border-danger/30 bg-danger-soft p-s3 text-base text-danger"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                This is not who the church was created for. It was set up for{' '}
                <strong>{request.invited_mobile ?? request.invited_email}</strong>.
              </span>
            </p>
          ) : null}
        </div>

        <div className="flex gap-s2">
          <Button
            variant="primary"
            onClick={() => setPending('approve')}
            loading={decide.isPending}
          >
            <Check className="h-4 w-4" aria-hidden />
            Approve
          </Button>
          <Button
            variant="danger"
            onClick={() => setPending('reject')}
            loading={decide.isPending}
          >
            <X className="h-4 w-4" aria-hidden />
            Reject
          </Button>
        </div>
      </Card>

      {pending ? (
        <Confirm
          open
          onOpenChange={(open) => {
            if (!open) setPending(null)
          }}
          title={
            pending === 'approve'
              ? `Approve ${request.church_name}?`
              : `Reject ${request.church_name}?`
          }
          body={
            pending === 'approve'
              ? request.matches_invite === false
                /* Repeated at the moment of the decision, not only on the card
                   behind it. This is the click that hands over the church. */
                ? `${request.admin_name} is not who this church was created for. Approving makes them its administrator.`
                : `${request.admin_name} becomes its administrator and can start adding members.`
              : `The church stays unusable and ${request.admin_name} does not become its administrator.`
          }
          confirmLabel={pending === 'approve' ? 'Approve church' : 'Reject request'}
          tone={
            pending === 'approve' && request.matches_invite !== false ? 'primary' : 'danger'
          }
          loading={decide.isPending}
          onConfirm={() =>
            decide.mutate(
              { requestId: request.request_id, approve: pending === 'approve' },
              { onSettled: () => setPending(null) },
            )
          }
        />
      ) : null}
    </>
  )
}
